import React from 'react';
import { Star } from 'lucide-react';
import { EVENT_THEMES } from '@/shared/constants/event-types.v4';
import { Event } from '@/types/zod.schemas';
import { formatMacro } from '@shared/utils/formatting';

interface TimelineActivityProps {
    act: Event & { top: number; height: number; column: number; totalCols: number };
    isZooming: boolean;
    labelFontSize: number;
    icon: any;
    onSelect: (event: Event) => void;
}

/**
 * TimelineActivity: A height-aware visual block representing an event.
 * SHADOW VERSION (.tsx)
 */
export const TimelineActivity: React.FC<TimelineActivityProps> = ({
    act,
    isZooming,
    labelFontSize,
    icon: Icon,
    onSelect
}) => {
    const status = act.status;
    const type = act.type;
    const metrics = (act.preview_data as any) || {};
    const themeColor = EVENT_THEMES[type] || EVENT_THEMES.TASK;
    
    const isSkipped = status === 'SKIPPED';
    const isPlanned = status === 'PLANNED';
    const isCompleted = status === 'COMPLETED';
    const isOverdue = isPlanned && new Date(act.start_time).getTime() < new Date().getTime();

    let baseColor = themeColor;
    if (isSkipped) baseColor = '#444';
    if (isOverdue) baseColor = '#ef4444';

    const itemBgColor = isSkipped ? 'rgba(68, 68, 68, 0.1)' : 
                       isOverdue ? 'rgba(239, 68, 68, 0.1)' :
                       `${themeColor}26`;

    return (
        <div
            onClick={(e) => { e.stopPropagation(); onSelect(act); }}
            className={isOverdue ? 'animate-pulse' : ''}
            style={{
                position: 'absolute',
                top: 0,
                transform: `translateY(${act.top}px)`,
                left: `${(act.column * 100) / act.totalCols}%`,
                width: `${100 / act.totalCols}%`,
                height: `${act.height}px`,
                backgroundColor: itemBgColor,
                borderLeft: `4px solid ${baseColor}`,
                borderRadius: '4px',
                padding: act.height < 35 ? '2px 4px' : '8px 10px',
                cursor: 'pointer',
                pointerEvents: 'auto',
                overflow: 'hidden',
                zIndex: 10 + act.column,
                boxSizing: 'border-box',
                opacity: isSkipped ? 0.5 : 1,
                borderRight: act.totalCols > 1 ? '1px solid #000' : 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                willChange: 'height, top'
            }}
        >
            <h4 style={{ fontSize: `${Math.max(8, labelFontSize + 1)}px`, fontWeight: '900', color: isSkipped ? '#666' : '#fff', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0, lineHeight: 1 }}>
                {act.label}
            </h4>

            {!isZooming && act.height > 45 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon size={12} color={baseColor} fill={isCompleted ? baseColor : 'transparent'} style={{ opacity: isPlanned ? 0.5 : 1 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                        <div style={{ fontSize: '10px', color: baseColor, fontWeight: '900', textTransform: 'uppercase' }}>
                            {isOverdue ? 'OVERDUE' : (isPlanned ? 'PLAN' : act.summary)}
                        </div>
                        {isCompleted && metrics.quality_rating > 0 && (
                            <div style={{ display: 'flex', gap: '1px' }}>
                                {[...Array(metrics.quality_rating)].map((_, i) => <Star key={i} size={8} color={baseColor} fill={baseColor} />)}
                            </div>
                        )}
                        {isCompleted && type === 'NUTRITION' && metrics.calories && (
                            <div style={{ fontSize: '9px', fontWeight: '950', color: '#fff', opacity: 0.9 }}>
                                {formatMacro(metrics.calories)} <span style={{ color: baseColor }}>KC</span> 
                                {metrics.protein && <span style={{ marginLeft: '4px' }}>{formatMacro(metrics.protein)}<span style={{ color: '#3b82f6' }}>P</span></span>}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* CONDENSED MODE */}
            {!isZooming && act.height <= 45 && act.height > 25 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: 'auto' }}>
                    <Icon size={10} color={baseColor} fill={isCompleted ? baseColor : 'transparent'} />
                    {isOverdue && <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#ef4444' }} />}
                </div>
            )}
        </div>
    );
};
